// components/PicnicManager.js
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Table,
  Button,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Space,
  message,
  Popconfirm,
  Tag,
  Row,
  Col,
  Card
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
  CoffeeOutlined,
  ShoppingOutlined,
  ForkOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import { BASE_URL } from '../utils/constants';

const { Option } = Select;

const GestionPicnics = ({ visible, onClose, onSuccess }) => {
  const [picnics, setPicnics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalFormVisible, setModalFormVisible] = useState(false);
  const [editingPicnic, setEditingPicnic] = useState(null);
  const [form] = Form.useForm();
  
  // Filtros
  const [fechaDesde, setFechaDesde] = useState(null);
  const [fechaHasta, setFechaHasta] = useState(null);
  const [numHab, setNumHab] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  const tipoOptions = [
    { value: 'D', label: 'Desayuno', icon: <CoffeeOutlined />, color: 'orange' },
    { value: 'A', label: 'Almuerzo', icon: <ShoppingOutlined />, color: 'green' },
    { value: 'C', label: 'Cena', icon: <ForkOutlined />, color: 'blue' }
  ];

  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'FechaPicnic',
      key: 'FechaPicnic',
      width: 120,
      render: (fecha) => fecha ? dayjs(fecha).format('DD/MM/YYYY') : '-',
      sorter: (a, b) => new Date(a.FechaPicnic) - new Date(b.FechaPicnic)
    },
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      render: (num) => num === 0 ? <Tag>General</Tag> : <Tag color="blue">{num}</Tag>
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 100,
      render: (tipo) => {
        const tipoInfo = tipoOptions.find(t => t.value === tipo);
        return (
          <Tag color={tipoInfo?.color} icon={tipoInfo?.icon}>
            {tipoInfo?.label}
          </Tag>
        );
      }
    },
    {
      title: 'Cantidad',
      dataIndex: 'NumPicnic',
      key: 'NumPicnic',
      width: 100,
      render: (num) => <Tag color="purple" style={{ fontWeight: 'bold' }}>{num}</Tag>
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="¿Eliminar picnic?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleDelete(record.numero)}
            okText="Sí"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const fetchPicnics = async (params = {}) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const queryParams = {
        page: params.page || pagination.current,
        pageSize: params.pageSize || pagination.pageSize,
        ...(fechaDesde && { fechaDesde: fechaDesde.format('YYYY-MM-DD') }),
        ...(fechaHasta && { fechaHasta: fechaHasta.format('YYYY-MM-DD') }),
        ...(numHab && numHab !== '' && { numHab })
      };
      
      const response = await axios.get(`${BASE_URL}/api/serviciococina/picnics`, {
        headers: { Authorization: `Bearer ${token}` },
        params: queryParams
      });
      
      setPicnics(response.data.data || []);
      if (response.data.pagination) {
        setPagination({
          ...pagination,
          total: response.data.pagination.totalRecords,
          current: response.data.pagination.currentPage,
          pageSize: response.data.pagination.pageSize
        });
      }
    } catch (error) {
      console.error('Error cargando picnics:', error);
      message.error('Error al cargar los picnics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible) {
      fetchPicnics();
    }
  }, [visible, fechaDesde, fechaHasta, numHab]);

  const handleCreate = () => {
    setEditingPicnic(null);
    form.resetFields();
    form.setFieldsValue({
      NumHab: 0,
      tipo: 'D',
      NumPicnic: 1
    });
    setModalFormVisible(true);
  };

  const handleEdit = (record) => {
    setEditingPicnic(record);
    form.setFieldsValue({
      NumHab: record.NumHab,
      FechaPicnic: dayjs(record.FechaPicnic),
      tipo: record.tipo,
      NumPicnic: record.NumPicnic
    });
    setModalFormVisible(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('token');
      
      const data = {
        NumHab: values.NumHab || 0,
        FechaPicnic: values.FechaPicnic.format('YYYY-MM-DD'),
        tipo: values.tipo,
        NumPicnic: values.NumPicnic
      };
      
      if (editingPicnic) {
        await axios.put(
          `${BASE_URL}/api/serviciococina/picnics/${editingPicnic.numero}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Picnic actualizado correctamente');
      } else {
        await axios.post(
          `${BASE_URL}/api/serviciococina/picnics`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        message.success('Picnic creado correctamente');
      }
      
      setModalFormVisible(false);
      fetchPicnics();
      if (onSuccess) onSuccess();
      
    } catch (error) {
      console.error('Error guardando picnic:', error);
      message.error(error.response?.data?.error || 'Error al guardar');
    }
  };

  const handleDelete = async (numero) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BASE_URL}/api/serviciococina/picnics/${numero}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      message.success('Picnic eliminado correctamente');
      fetchPicnics();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error('Error eliminando picnic:', error);
      message.error('Error al eliminar');
    }
  };

  const handleResetFilters = () => {
    setFechaDesde(null);
    setFechaHasta(null);
    setNumHab('');
    setPagination({ ...pagination, current: 1 });
  };

  return (
    <>
      <Modal
        title="Gestión de Picnics"
        open={visible}
        onCancel={onClose}
        footer={null}
        width={1000}
        destroyOnClose
      >
        <Card size="small" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]} align="middle">
            <Col span={8}>
              <DatePicker
                placeholder="Fecha desde"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={fechaDesde}
                onChange={setFechaDesde}
              />
            </Col>
            <Col span={8}>
              <DatePicker
                placeholder="Fecha hasta"
                style={{ width: '100%' }}
                format="DD/MM/YYYY"
                value={fechaHasta}
                onChange={setFechaHasta}
              />
            </Col>
            <Col span={6}>
              <Input
                placeholder="Nº Habitación"
                value={numHab}
                onChange={(e) => setNumHab(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Col>
            <Col span={2}>
              <Button
                icon={<ReloadOutlined />}
                onClick={handleResetFilters}
              />
            </Col>
          </Row>
        </Card>
        
        <div style={{ marginBottom: 16, textAlign: 'right' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Nuevo Picnic
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={picnics}
          rowKey="numero"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} picnics`,
            onChange: (page, pageSize) => {
              setPagination({ ...pagination, current: page, pageSize });
              fetchPicnics({ page, pageSize });
            }
          }}
          scroll={{ x: 700 }}
        />
      </Modal>

<Modal
  title={editingPicnic ? 'Editar Picnic' : 'Nuevo Picnic'}
  open={modalFormVisible}
  onOk={handleSave}
  onCancel={() => setModalFormVisible(false)}
  okText={editingPicnic ? 'Actualizar' : 'Crear'}
  cancelText="Cancelar"
  destroyOnClose
  getContainer={document.body}  // Esto lo renderiza fuera del modal padre
  style={{ top: 20 }}
>

        <Form
            form={form}
            layout="vertical"
            initialValues={{ NumHab: 0, tipo: 'D', NumPicnic: 1 }}
        >
            <Form.Item
            name="FechaPicnic"
            label="Fecha"
            rules={[{ required: true, message: 'Seleccione la fecha' }]}
            >
            <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
            </Form.Item>
            
            <Form.Item
            name="NumHab"
            label="Nº Habitación"
            tooltip="Dejar en blanco o 0 para picnic general"
            >
            <InputNumber
                style={{ width: '100%' }}
                placeholder="0 = General"
                min={0}
            />
            </Form.Item>
            
            <Form.Item
            name="tipo"
            label="Tipo de Servicio"
            rules={[{ required: true, message: 'Seleccione el tipo' }]}
            >
            <Select>
                {tipoOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>
                    <Space>
                    {opt.icon}
                    {opt.label}
                    </Space>
                </Option>
                ))}
            </Select>
            </Form.Item>
            
            <Form.Item
            name="NumPicnic"
            label="Número de Picnics"
            rules={[{ required: true, message: 'Ingrese la cantidad' }]}
            >
            <InputNumber
                style={{ width: '100%' }}
                min={1}
                placeholder="Cantidad de picnics"
            />
            </Form.Item>
        </Form>
        </Modal>
    </>
  );
};

export default GestionPicnics;